import { BugIcon } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';

import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/use-auth';

// A "Send feedback" row in the app sidebar's footer. It used to be a fixed
// round button in the bottom-right corner, and that sat on top of right rails
// at every scroll position (the Concept design page's inspector lost its score
// column behind it). Content areas own the right edge; the sidebar footer is
// chrome that no page scrolls under, and it is reachable on mobile through the
// sidebar sheet, where the corner button was hidden.
//
// Monochrome (Law 2 — a utility affordance, not a state).
//
// Signed-in only: this is an internal bug-reporting affordance, so it has no
// business on the public auth screens (sign in / sign up / password reset),
// where it was previously visible to anyone who loaded the login page.
//
// Sentry init is deferred to an idle callback and PROD-gated (see
// src/instrument.ts / src/main.tsx), so Sentry.getFeedback() isn't
// synchronously available on mount. Poll briefly until it is; no-ops in
// dev, where Sentry never initializes.
export function SentryFeedbackTrigger() {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { user, loading } = useAuth();
  const isSignedIn = Boolean(user) && !loading;

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (!isSignedIn) return;

    let cancelled = false;
    let unbind: (() => void) | undefined;

    const tryAttach = async () => {
      try {
        const Sentry = await import('@sentry/react');
        const feedback = Sentry.getFeedback?.();
        const button = buttonRef.current;
        if (cancelled || !feedback || !button) return false;

        const result = feedback.attachTo(button, { formTitle: 'Report a Bug' });
        if (typeof result === 'function') unbind = result;
        return true;
      } catch {
        return false;
      }
    };

    let intervalId: ReturnType<typeof setInterval> | undefined;
    tryAttach().then((attached) => {
      if (attached || cancelled) return;
      intervalId = setInterval(() => {
        tryAttach().then((ok) => {
          if (ok && intervalId) clearInterval(intervalId);
        });
      }, 500);
    });

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      unbind?.();
    };
  }, [isSignedIn]);

  if (!import.meta.env.PROD) return null;
  if (!isSignedIn) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton ref={buttonRef} type="button" tooltip="Send feedback">
          <BugIcon className="size-4" weight="regular" aria-hidden="true" />
          <span>Send feedback</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
